import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const GATEWAY_ICONS = {
  paystack: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="4" rx="1" fill="#00C3F7"/><rect x="3" y="9" width="18" height="4" rx="1" fill="#00C3F7"/><rect x="3" y="15" width="12" height="4" rx="1" fill="#00C3F7"/></svg>
  ),
  flutterwave: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-5-5 1.41-1.41L11 14.17l7.59-7.59L20 8l-9 9z" fill="#F5A623"/></svg>
  ),
  remita: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#E8632B" strokeWidth="2"/><path d="M8 12h8M12 8v8" stroke="#E8632B" strokeWidth="2" strokeLinecap="round"/></svg>
  ),
  manual: (
    <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>
  ),
};

const GATEWAY_COLORS = {
  paystack: 'border-[#00C3F7] bg-[#00C3F7]/5',
  flutterwave: 'border-[#F5A623] bg-[#F5A623]/5',
  remita: 'border-[#E8632B] bg-[#E8632B]/5',
  manual: 'border-gray-400 bg-gray-50',
};

// Dynamically load Paystack Inline JS
function loadPaystackScript() {
  return new Promise((resolve, reject) => {
    if (window.PaystackPop) return resolve();
    // Remove any previously failed script tag so we get a fresh load
    const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => {
      if (window.PaystackPop) resolve();
      else reject(new Error('Paystack script loaded but PaystackPop is not available'));
    };
    script.onerror = () => reject(new Error('Failed to load Paystack script. Check your internet connection.'));
    document.head.appendChild(script);
  });
}

export default function Payments() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showGatewayPopup, setShowGatewayPopup] = useState(false);
  const [gateways, setGateways] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBankTransferModal, setShowBankTransferModal] = useState(false);
  const [bankTransferData, setBankTransferData] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [formData, setFormData] = useState({
    payment_type: 'annual_dues', amount: '', description: '',
    date: new Date().toISOString().split('T')[0],
  });

  const fetchPayments = useCallback(() => {
    api.get('/payments/my/').then(res => setPayments(res.data.results || res.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  // Preload Paystack script on page mount so it's ready instantly
  useEffect(() => { loadPaystackScript().catch(() => {}); }, []);

  const openGatewayPopup = (e) => {
    e.preventDefault();
    if (!formData.amount || Number(formData.amount) <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }
    setGatewaysLoading(true);
    setShowGatewayPopup(true);
    setSelectedGateway(null);
    api.get('/payment-gateways/active/')
      .then(res => setGateways(res.data.results || res.data || []))
      .catch(() => { toast.error('Failed to load payment options.'); setShowGatewayPopup(false); })
      .finally(() => setGatewaysLoading(false));
  };

  const handleInitiate = async () => {
    if (!selectedGateway) { toast.error('Please select a payment method.'); return; }
    setSubmitting(true);
    try {
      const ref = `PAY-${Date.now()}-${Math.floor(1000 + Math.random() * 9000)}`;
      const res = await api.post('/payments/initiate/', {
        ...formData, reference: ref, payment_method: selectedGateway.gateway,
      });

      if (selectedGateway.gateway === 'paystack') {
        // --- Paystack Inline Popup ---
        try {
          await loadPaystackScript();
        } catch (err) {
          toast.error(err?.message || 'Could not load Paystack. Please try again.');
          setSubmitting(false);
          return;
        }

        if (!window.PaystackPop) {
          toast.error('Paystack is not available. Please refresh and try again.');
          setSubmitting(false);
          return;
        }

        const email = res.data.email || user?.email || '';
        const handler = window.PaystackPop.setup({
          key: selectedGateway.public_key,
          email: email,
          amount: Math.round(Number(formData.amount) * 100), // kobo
          currency: 'NGN',
          ref: ref,
          label: selectedGateway.label || '',
          metadata: {
            payment_type: formData.payment_type,
            custom_fields: [
              { display_name: 'Payment Type', variable_name: 'payment_type', value: formData.payment_type },
            ],
          },
          onClose: () => {
            toast.warning('Payment window closed. Your payment is still pending.', { autoClose: 8000 });
            setPayments(prev => [res.data, ...prev]);
            setShowForm(false);
            setShowGatewayPopup(false);
            setSelectedGateway(null);
            setFormData({ payment_type: 'annual_dues', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
          },
          callback: (response) => {
            api.post(`/payments/verify/${ref}/`, {
              transaction_id: response.reference || response.trxref,
              payment_method: 'paystack',
            })
              .then(() => {
                toast.success('Payment completed successfully!');
                fetchPayments();
              })
              .catch(() => {
                toast.error('Payment received but verification failed. Contact admin.');
              })
              .finally(() => {
                setShowForm(false);
                setShowGatewayPopup(false);
                setSelectedGateway(null);
                setFormData({ payment_type: 'annual_dues', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
              });
          },
        });
        handler.openIframe();
        setSubmitting(false);
        return;
      }

      // --- Manual / Other Gateways ---
      setPayments(prev => [res.data, ...prev]);
      setShowForm(false);
      setShowGatewayPopup(false);
      setSelectedGateway(null);

      if (selectedGateway.gateway === 'manual') {
        setBankTransferData({
          bankName: selectedGateway.bank_name,
          accountNumber: selectedGateway.account_number,
          accountName: selectedGateway.account_name,
          amount: Number(formData.amount),
          reference: ref,
          paymentType: formData.payment_type,
          memberName: user?.profile?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || '—',
          memberEmail: user?.email || '—',
          memberPhone: user?.profile?.phone_number || '—',
          membershipNumber: user?.profile?.membership_number || '—',
        });
        setShowBankTransferModal(true);
      } else {
        toast.success('Payment initiated successfully. Proceed to complete payment.');
      }
      setFormData({ payment_type: 'annual_dues', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
    } catch (err) {
      const msg = err?.response?.data?.detail
        || err?.response?.data?.message
        || (err?.response?.data && typeof err.response.data === 'object'
            ? Object.values(err.response.data).flat().join(', ')
            : null)
        || err?.message
        || 'Failed to initiate payment.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition"
        >
          {showForm ? 'Cancel' : 'Make Payment'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={openGatewayPopup} className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Payment Type</label>
              <select value={formData.payment_type} onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
                <option value="annual_dues">Annual Dues</option>
                <option value="registration">Registration</option>
                <option value="event">Event Fee</option>
                <option value="agm">AGM</option>
                <option value="mcpd">MCPD</option>
                <option value="form">Form</option>
                <option value="hotel">Hotel</option>
                <option value="penalty">Penalty</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Amount (₦)</label>
              <input type="number" required value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Date</label>
              <input type="date" required value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
              <input type="text" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
            </div>
          </div>
          <button type="submit" className="mt-4 bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#062E10] transition flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
            Choose Payment Method
          </button>
        </form>
      )}

      {/* Payment Gateway Selection Popup */}
      {showGatewayPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setShowGatewayPopup(false); setSelectedGateway(null); }}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0B5D1E] to-[#1A7A35] px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-lg">Select Payment Method</h3>
                  <p className="text-white/70 text-sm mt-0.5">
                    {formData.payment_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} — ₦{Number(formData.amount).toLocaleString()}
                  </p>
                </div>
                <button onClick={() => { setShowGatewayPopup(false); setSelectedGateway(null); }}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Gateway Options */}
            <div className="p-6">
              {gatewaysLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-8 h-8 border-[3px] border-[#0B5D1E] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : gateways.length === 0 ? (
                <div className="text-center py-10">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-gray-500 font-medium">No payment gateways available</p>
                  <p className="text-gray-400 text-sm mt-1">Please contact the admin to configure payment options.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {gateways.map(gw => (
                    <button
                      key={gw.id}
                      type="button"
                      onClick={() => setSelectedGateway(gw)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                        selectedGateway?.id === gw.id
                          ? `${GATEWAY_COLORS[gw.gateway] || 'border-[#0B5D1E] bg-[#0B5D1E]/5'} shadow-md ring-1 ring-offset-1 ${
                              gw.gateway === 'paystack' ? 'ring-[#00C3F7]' :
                              gw.gateway === 'flutterwave' ? 'ring-[#F5A623]' :
                              gw.gateway === 'remita' ? 'ring-[#E8632B]' : 'ring-gray-400'
                            }`
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex-shrink-0">
                        {GATEWAY_ICONS[gw.gateway] || GATEWAY_ICONS.manual}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900">{gw.gateway_display}</div>
                        {gw.gateway === 'manual' && gw.bank_name && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {gw.bank_name} — {gw.account_number} ({gw.account_name})
                          </p>
                        )}
                        {gw.gateway !== 'manual' && (
                          <p className="text-xs text-gray-500 mt-0.5">Pay securely online</p>
                        )}
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedGateway?.id === gw.id ? 'border-[#0B5D1E] bg-[#0B5D1E]' : 'border-gray-300'
                      }`}>
                        {selectedGateway?.id === gw.id && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {gateways.length > 0 && (
              <div className="px-6 pb-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowGatewayPopup(false); setSelectedGateway(null); }}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleInitiate}
                  disabled={!selectedGateway || submitting}
                  className={`flex-1 px-4 py-3 rounded-xl text-sm font-bold transition flex items-center justify-center gap-2 ${
                    selectedGateway && !submitting
                      ? 'bg-[#0B5D1E] text-white hover:bg-[#062E10] shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {submitting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Proceed to Pay
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bank Transfer Details Modal */}
      {showBankTransferModal && bankTransferData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowBankTransferModal(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0B5D1E] to-[#1A7A35] px-6 py-4 text-center flex-shrink-0">
              <div className="w-10 h-10 bg-white/15 rounded-full flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
              </div>
              <h3 className="text-white font-bold text-base">Bank Transfer Details</h3>
              <p className="text-white/70 text-xs mt-0.5">Complete your payment via bank transfer</p>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Amount */}
              <div className="text-center bg-green-50 rounded-xl py-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Amount to Transfer</p>
                <p className="text-2xl font-bold text-[#0B5D1E] mt-0.5">₦{bankTransferData.amount.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1 capitalize">{bankTransferData.paymentType.replace('_', ' ')}</p>
              </div>

              {/* Bank Account Details */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                  Transfer To
                </h4>
                <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Bank</span>
                    <span className="text-sm font-semibold text-gray-900">{bankTransferData.bankName}</span>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Account Number</span>
                    <span className="text-sm font-bold text-gray-900 tracking-wider">{bankTransferData.accountNumber}</span>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Account Name</span>
                    <span className="text-sm font-semibold text-gray-900">{bankTransferData.accountName}</span>
                  </div>
                </div>
              </div>

              {/* Member Details */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  Member Details
                </h4>
                <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Name</span>
                    <span className="text-sm font-semibold text-gray-900">{bankTransferData.memberName}</span>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Email</span>
                    <span className="text-sm text-gray-900">{bankTransferData.memberEmail}</span>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Phone</span>
                    <span className="text-sm text-gray-900">{bankTransferData.memberPhone}</span>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Membership No.</span>
                    <span className="text-sm font-semibold text-gray-900">{bankTransferData.membershipNumber}</span>
                  </div>
                </div>
              </div>

              {/* Payment Reference */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-600 font-medium">Payment Reference</p>
                    <p className="text-base font-bold text-amber-800 mt-0.5 font-mono">{bankTransferData.reference}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(bankTransferData.reference);
                      toast.success('Reference copied!', { autoClose: 2000 });
                    }}
                    className="px-3 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-semibold transition flex items-center gap-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                    Copy
                  </button>
                </div>
                <p className="text-[11px] text-amber-600 mt-2">Use this reference when making your transfer so we can identify your payment.</p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 pb-4 pt-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  setShowBankTransferModal(false);
                  setBankTransferData(null);
                  setShowForm(false);
                  setFormData({ payment_type: 'annual_dues', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
                }}
                className="w-full py-3 rounded-xl bg-[#0B5D1E] text-white font-bold text-sm hover:bg-[#062E10] transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">S/N</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Reference</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Method</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Year</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan="8" className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : payments.length === 0 ? (
              <tr><td colSpan="8" className="text-center py-8 text-gray-500">No payment records.</td></tr>
            ) : payments.map((p, idx) => (
              <tr key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedPayment(p)}>
                <td className="px-6 py-4 text-sm">{idx + 1}</td>
                <td className="px-6 py-4 text-sm font-mono">{p.reference?.length > 5 ? p.reference.replace(/\D/g, '').slice(-5).padStart(5, '0') : p.reference}</td>
                <td className="px-6 py-4 text-sm capitalize">{p.payment_type?.replace('_', ' ')}</td>
                <td className="px-6 py-4 text-sm font-semibold">₦{Number(p.amount).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${
                    p.payment_method === 'paystack' ? 'bg-blue-100 text-blue-700' :
                    p.payment_method === 'manual' ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {p.payment_method === 'paystack' ? 'Paystack' : p.payment_method === 'manual' ? 'Bank Transfer' : p.payment_method || '—'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">{p.year}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    p.status === 'completed' ? 'bg-green-100 text-green-700' :
                    p.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                  }`}>{p.status}</span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{new Date(p.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payment Detail Modal */}
      {selectedPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPayment(null)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0B5D1E] to-[#1A7A35] px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-base">Payment Details</h3>
                  <p className="text-white/70 text-xs mt-0.5">Reference: {selectedPayment.reference}</p>
                </div>
                <button onClick={() => setSelectedPayment(null)}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Status & Amount */}
              <div className="text-center bg-gray-50 rounded-xl py-4">
                <span className={`inline-block text-xs px-3 py-1 rounded-full font-semibold mb-2 ${
                  selectedPayment.status === 'completed' ? 'bg-green-100 text-green-700' :
                  selectedPayment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  selectedPayment.status === 'refunded' ? 'bg-purple-100 text-purple-700' :
                  'bg-red-100 text-red-700'
                }`}>{selectedPayment.status?.toUpperCase()}</span>
                <p className="text-2xl font-bold text-gray-900">₦{Number(selectedPayment.amount).toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1 capitalize">{selectedPayment.payment_type?.replace('_', ' ')}</p>
              </div>

              {/* Payment Info */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Payment Method</span>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${
                    selectedPayment.payment_method === 'paystack' ? 'bg-blue-100 text-blue-700' :
                    selectedPayment.payment_method === 'manual' ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {selectedPayment.payment_method === 'paystack' ? 'Paystack' : selectedPayment.payment_method === 'manual' ? 'Bank Transfer' : selectedPayment.payment_method || '—'}
                  </span>
                </div>
                <div className="border-t border-gray-200" />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Reference</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-semibold text-gray-900">{selectedPayment.reference}</span>
                    <button type="button" onClick={() => { navigator.clipboard.writeText(selectedPayment.reference); toast.success('Copied!', { autoClose: 1500 }); }}
                      className="text-gray-400 hover:text-gray-600 transition">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                    </button>
                  </div>
                </div>
                {selectedPayment.transaction_id && (
                  <>
                    <div className="border-t border-gray-200" />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Transaction ID</span>
                      <span className="text-sm font-mono text-gray-700">{selectedPayment.transaction_id}</span>
                    </div>
                  </>
                )}
                <div className="border-t border-gray-200" />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Year</span>
                  <span className="text-sm font-semibold text-gray-900">{selectedPayment.year}</span>
                </div>
                <div className="border-t border-gray-200" />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Date Initiated</span>
                  <span className="text-sm text-gray-900">{new Date(selectedPayment.created_at).toLocaleString()}</span>
                </div>
                {selectedPayment.paid_at && (
                  <>
                    <div className="border-t border-gray-200" />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Date Paid</span>
                      <span className="text-sm text-gray-900">{new Date(selectedPayment.paid_at).toLocaleString()}</span>
                    </div>
                  </>
                )}
                {selectedPayment.receipt_number && (
                  <>
                    <div className="border-t border-gray-200" />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Receipt No.</span>
                      <span className="text-sm font-semibold text-gray-900">{selectedPayment.receipt_number}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Description */}
              {selectedPayment.description && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="text-sm text-gray-800">{selectedPayment.description}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 pb-4 pt-2 flex-shrink-0">
              <button type="button" onClick={() => setSelectedPayment(null)}
                className="w-full py-3 rounded-xl bg-[#0B5D1E] text-white font-bold text-sm hover:bg-[#062E10] transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
